
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Download, FileSpreadsheet, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ImportPasswordProtection } from "@/components/auth/ImportPasswordProtection";
import { downloadCSVTemplate, getTemplateInstructions } from "./import-dialog/templateUtils";
import { ImportingStage } from "./import-dialog/ImportingStage";

interface ContentIdImportDialogProps {
  onDataImported?: () => void;
}

export function ContentIdImportDialog({ onDataImported }: ContentIdImportDialogProps) {
  const { toast } = useToast();
  const { hasFullAccess } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importStage, setImportStage] = useState<'select' | 'importing' | 'complete'>('select');
  const [importProgress, setImportProgress] = useState(0);
  const [currentItem, setCurrentItem] = useState<string>('');
  const [processingDetails, setProcessingDetails] = useState<{
    current: number;
    total: number;
    detail?: string;
  } | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'text/csv') {
      setSelectedFile(file);
    } else {
      toast({
        title: "Invalid File",
        description: "Please select a valid CSV file",
        variant: "destructive"
      });
    }
  };

  const parseCSV = (text: string): string[][] => {
    const lines = text.split('\n').filter(line => line.trim());
    return lines.map(line => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result.map(cell => cell.replace(/^"|"$/g, ''));
    });
  };

  const formatDateFromCSV = (dateStr: string): string | null => {
    if (!dateStr || dateStr.trim() === '') return null;
    
    // Handle MM/DD/YYYY format
    const mmddyyyyMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (mmddyyyyMatch) {
      const [, month, day, year] = mmddyyyyMatch;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    
    // Handle other common formats as fallback
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    return null;
  };

  const combineTagColumns = (rowData: any): string[] => {
    const tagColumns = ['tag1', 'tag2', 'tag3', 'tag4', 'tag5'];
    const tags: string[] = [];
    
    tagColumns.forEach(tagCol => {
      if (rowData[tagCol] && rowData[tagCol].trim()) {
        tags.push(rowData[tagCol].trim());
      }
    });
    
    return tags;
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: "No File Selected",
        description: "Please select a CSV file to import",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    setImportStage('importing');
    setImportProgress(0);
    
    try {
      console.log('Starting import process...');
      const text = await selectedFile.text();
      const rows = parseCSV(text);
      
      if (rows.length < 2) {
        throw new Error("CSV file must contain headers and at least one data row");
      }

      const headers = rows[0];
      const dataRows = rows.slice(1);
      console.log('CSV parsed successfully:', { headerCount: headers.length, dataRowCount: dataRows.length });

      // Validate required headers
      const requiredHeaders = ['content_id'];
      const missingRequired = requiredHeaders.filter(header => !headers.includes(header));
      
      if (missingRequired.length > 0) {
        throw new Error(`Missing required headers: ${missingRequired.join(', ')}`);
      }

      setProcessingDetails({
        current: 0,
        total: dataRows.length,
        detail: 'Processing CSV data...'
      });

      // Process each row with detailed progress updates
      const importData = [];
      for (let index = 0; index < dataRows.length; index++) {
        const row = dataRows[index];
        const rowData: any = {};
        
        headers.forEach((header, headerIndex) => {
          rowData[header] = row[headerIndex] || null;
        });

        // Update progress for processing
        const processingProgress = Math.round(((index + 1) / dataRows.length) * 50); // First 50% for processing
        setImportProgress(processingProgress);
        setCurrentItem(`Processing: ${rowData.content_id || 'Unknown'}`);
        setProcessingDetails({
          current: index + 1,
          total: dataRows.length,
          detail: `Processing row ${index + 1} of ${dataRows.length}`
        });

        // Small delay to show progress
        if (index % 10 === 0) {
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        // Combine tag columns into a single array
        const tags = combineTagColumns(rowData);

        // Parse pic field
        const pic = rowData.pic ? rowData.pic.split(';').map((p: string) => p.trim()).filter(Boolean) : [];

        // Format ticket_time
        const formattedTicketTime = formatDateFromCSV(rowData.ticket_time);

        const processedItem = {
          content_id: rowData.content_id?.trim(),
          ticket_time: formattedTicketTime,
          description: rowData.description?.trim() || null,
          tags: tags,
          pic: pic,
          type: 'Content',
          name: rowData.content_id?.trim()
        };

        console.log(`Processed item ${index + 1}:`, processedItem);
        importData.push(processedItem);
      }

      // Validate required fields
      const invalidRows = importData.filter(item => !item.content_id);
      if (invalidRows.length > 0) {
        throw new Error(`Found ${invalidRows.length} rows with missing content_id`);
      }

      console.log('All rows processed successfully, starting database insert...');
      setImportProgress(75);
      setCurrentItem('Saving to database...');
      setProcessingDetails({
        current: dataRows.length,
        total: dataRows.length,
        detail: 'Inserting data into database...'
      });

      // Insert data into database
      console.log('Inserting data:', importData);
      const { data, error } = await supabase
        .from('topics_business_units')
        .insert(importData)
        .select();

      if (error) {
        console.error('Database insert error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      console.log('Database insert successful:', data);
      setImportProgress(100);
      setCurrentItem('Import completed successfully!');
      
      // Show completion
      setTimeout(() => {
        toast({
          title: "Import Successful",
          description: `Successfully imported ${importData.length} Content IDs`,
        });

        setSelectedFile(null);
        setImportStage('complete');
        setIsOpen(false);
        if (onDataImported) onDataImported();

        // Reset state after dialog closes
        setTimeout(() => {
          setImportStage('select');
          setImportProgress(0);
          setCurrentItem('');
          setProcessingDetails(null);
        }, 500);
      }, 1000);

    } catch (error: any) {
      console.error("Import error:", error);
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import data",
        variant: "destructive"
      });
      setImportStage('select');
      setImportProgress(0);
      setCurrentItem('');
      setProcessingDetails(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadCSVTemplate('content_id');
  };

  const instructions = getTemplateInstructions('content_id');

  const resetDialog = () => {
    setSelectedFile(null);
    setImportStage('select');
    setImportProgress(0);
    setCurrentItem('');
    setProcessingDetails(null);
  };

  return (
    <ImportPasswordProtection isLimitedUser={!hasFullAccess}>
      <Dialog open={isOpen} onOpenChange={(open) => {
        setIsOpen(open);
        if (!open) resetDialog();
      }}>
        <DialogTrigger asChild>
          <Button className="h-10 bg-green-600 hover:bg-green-700">
            <Upload className="h-4 w-4 mr-2" />
            Import Content IDs
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FileSpreadsheet className="h-6 w-6 text-green-600" />
              Import Content IDs
            </DialogTitle>
          </DialogHeader>
          
          {importStage === 'importing' ? (
            <ImportingStage
              progress={importProgress}
              isLightMode={true}
              currentItem={currentItem}
              processingDetails={processingDetails}
            />
          ) : (
            <div className="space-y-6">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Import Instructions</h4>
                    <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
                      {instructions.map((instruction, index) => (
                        <li key={index}>{instruction}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <Button 
                    onClick={handleDownloadTemplate}
                    variant="outline" 
                    className="w-full"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download CSV Template
                  </Button>
                </div>

                <div>
                  <Label htmlFor="csv-file" className="text-sm font-medium">
                    Select CSV File
                  </Label>
                  <Input
                    id="csv-file"
                    type="file"
                    accept=".csv"
                    onChange={handleFileSelect}
                    className="mt-1"
                  />
                  {selectedFile && (
                    <p className="text-sm text-muted-foreground mt-2">
                      Selected: {selectedFile.name}
                    </p>
                  )}
                </div>

                <Button 
                  onClick={handleImport}
                  disabled={!selectedFile || isLoading}
                  className="w-full bg-green-600 hover:bg-green-700"
                >
                  {isLoading ? "Importing..." : "Import Content IDs"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </ImportPasswordProtection>
  );
}
